import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * @typedef {Object} AgeGateRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLDivElement} backdrop - The backdrop overlay
 * @property {HTMLButtonElement} yesButton - The "Yes" confirmation button
 * @property {HTMLButtonElement} noButton - The "No" rejection button
 */

/**
 * Age Gate Component
 * Blocks site access until user confirms they are of legal drinking age.
 * 
 * @extends {Component<AgeGateRefs>}
 */
class AgeGateComponent extends Component {
  requiredRefs = ['dialog', 'backdrop', 'yesButton', 'noButton'];

  /** @type {string} Storage key for verification */
  static STORAGE_KEY = 'age-gate-verified';

  /** @type {number} Previous scroll position before modal opens */
  #previousScrollY = 0;

  /** @type {boolean} Whether the modal is currently open */
  #isOpen = false;

  connectedCallback() {
    super.connectedCallback();

    // Skip in theme editor
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      return;
    }

    // Skip on access-denied page (where underage users are redirected)
    const redirectUrl = this.dataset.redirectUrl || '/pages/access-denied';
    const currentPath = window.location.pathname;
    if (currentPath === redirectUrl || currentPath.startsWith(redirectUrl + '/')) {
      return;
    }

    // Check verification status on mount
    if (!this.#isVerified()) {
      this.#showModal();
    }

    // Listen for navigation events to re-check verification
    window.addEventListener('pageswap', this.#handleNavigation);
    window.addEventListener('pagereveal', this.#handleNavigation);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hideModal();
    window.removeEventListener('pageswap', this.#handleNavigation);
    window.removeEventListener('pagereveal', this.#handleNavigation);
  }

  /**
   * Handles navigation events to re-check verification
   * @param {Event} event - Navigation event
   */
  #handleNavigation = () => {
    // Skip on access-denied page
    const redirectUrl = this.dataset.redirectUrl || '/pages/access-denied';
    const currentPath = window.location.pathname;
    if (currentPath === redirectUrl || currentPath.startsWith(redirectUrl + '/')) {
      return;
    }

    // Re-check verification after navigation
    if (!this.#isVerified() && !this.#isOpen) {
      this.#showModal();
    }
  };

  /**
   * Checks if user is already verified and verification hasn't expired
   * @returns {boolean} True if verified and not expired
   */
  #isVerified() {
    const storageMethod = this.dataset.storageMethod || 'cookie';
    const expirationDays = Number(this.dataset.expirationDays) || 90;

    if (storageMethod === 'cookie') {
      return this.#isVerifiedCookie(expirationDays);
    } else {
      return this.#isVerifiedLocalStorage(expirationDays);
    }
  }

  /**
   * Checks verification status in cookie
   * @param {number} expirationDays - Days until expiry
   * @returns {boolean} True if verified and not expired
   */
  #isVerifiedCookie(expirationDays) {
    const cookies = document.cookie.split(';');
    const ageGateCookie = cookies.find(cookie => 
      cookie.trim().startsWith(`${AgeGateComponent.STORAGE_KEY}=`)
    );

    if (!ageGateCookie) return false;

    try {
      const value = ageGateCookie.split('=')[1];
      const data = JSON.parse(decodeURIComponent(value));
      
      if (!data.verified || !data.expires) return false;
      
      // Check if expired
      const now = Math.floor(Date.now() / 1000);
      return data.expires > now;
    } catch {
      return false;
    }
  }

  /**
   * Checks verification status in localStorage
   * @param {number} expirationDays - Days until expiry
   * @returns {boolean} True if verified and not expired
   */
  #isVerifiedLocalStorage(expirationDays) {
    try {
      const stored = localStorage.getItem(AgeGateComponent.STORAGE_KEY);
      if (!stored) return false;

      const data = JSON.parse(stored);
      if (!data.verified || !data.expires) return false;

      // Check if expired
      const now = Math.floor(Date.now() / 1000);
      return data.expires > now;
    } catch {
      return false;
    }
  }

  /**
   * Stores verification with expiry timestamp
   */
  #storeVerification() {
    const storageMethod = this.dataset.storageMethod || 'cookie';
    const expirationDays = Number(this.dataset.expirationDays) || 90;
    
    // Calculate expiry timestamp (seconds since epoch)
    const expires = Math.floor(Date.now() / 1000) + (expirationDays * 24 * 60 * 60);
    const data = { verified: true, expires };

    if (storageMethod === 'cookie') {
      this.#storeVerificationCookie(data, expirationDays);
    } else {
      this.#storeVerificationLocalStorage(data);
    }
  }

  /**
   * Stores verification in cookie
   * @param {Object} data - Verification data
   * @param {number} expirationDays - Days until expiry
   */
  #storeVerificationCookie(data, expirationDays) {
    const isSecure = window.location.protocol === 'https:';
    const maxAge = expirationDays * 24 * 60 * 60;
    const value = encodeURIComponent(JSON.stringify(data));
    
    let cookieString = `${AgeGateComponent.STORAGE_KEY}=${value}; Path=/; SameSite=Lax; Max-Age=${maxAge}`;
    
    if (isSecure) {
      cookieString += '; Secure';
    }

    document.cookie = cookieString;
  }

  /**
   * Stores verification in localStorage
   * @param {Object} data - Verification data
   */
  #storeVerificationLocalStorage(data) {
    try {
      localStorage.setItem(AgeGateComponent.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      // Fallback to cookie if localStorage fails
      console.warn('localStorage unavailable, falling back to cookie');
      this.#storeVerificationCookie(data, Number(this.dataset.expirationDays) || 90);
    }
  }

  /**
   * Shows the age gate modal
   */
  #showModal() {
    if (this.#isOpen) return;

    const { dialog, backdrop, yesButton } = this.refs;
    if (!dialog || !backdrop || !yesButton) return;

    // Remove hidden attribute to show the component
    this.removeAttribute('hidden');

    // Save current scroll position
    this.#previousScrollY = window.scrollY;

    // Prevent body scroll
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      document.body.style.overflow = 'hidden';

      // Show backdrop
      backdrop.setAttribute('data-visible', '');
      
      // Show dialog
      dialog.showModal();
      this.#isOpen = true;

      // Trap focus within dialog
      trapFocus(dialog);

      // Set initial focus to "Yes" button
      yesButton.focus();

      // Prevent ESC from closing (per requirements)
      dialog.addEventListener('cancel', this.#preventClose);
    });
  }

  /**
   * Hides the age gate modal
   */
  #hideModal() {
    if (!this.#isOpen) return;

    const { dialog, backdrop } = this.refs;
    if (!dialog || !backdrop) return;

    // Remove ESC prevention
    dialog.removeEventListener('cancel', this.#preventClose);

    // Remove focus trap
    removeTrapFocus();

    // Hide dialog
    dialog.close();
    this.#isOpen = false;

    // Hide backdrop
    backdrop.removeAttribute('data-visible');

    // Hide the component
    this.setAttribute('hidden', '');

    // Restore body scroll
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.overflow = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Prevents the dialog from closing (for ESC key)
   * @param {Event} event - Cancel event
   */
  #preventClose = (event) => {
    event.preventDefault();
    // Keep focus trapped - refocus the primary button
    if (this.refs.yesButton) {
      this.refs.yesButton.focus();
    }
  };

  /**
   * Handles "Yes" button click - user confirms they are 18+
   */
  handleYes = () => {
    this.#storeVerification();
    this.#hideModal();
  };

  /**
   * Handles "No" button click - user is underage, redirect
   */
  handleNo = () => {
    const redirectUrl = this.dataset.redirectUrl || '/pages/access-denied';
    window.location.href = redirectUrl;
  };
}

if (!customElements.get('age-gate-component')) {
  customElements.define('age-gate-component', AgeGateComponent);
}
